import type { StorageTempMappingChange, StorageTempMappingRow } from '@/types/products'
import type { StorageCondition, StorageConditionMeta } from '@/types/storage'

/**
 * Draft-state helpers for the Storage Temp Mapping dialog (Configure →
 * Products). Pure so they are covered: this repo's vitest only collects
 * `src/**\/*.test.ts`, and logic left in the .tsx would have none.
 *
 * The draft is keyed by `temperatureKey` and holds only what the user touched;
 * a row absent from it keeps its saved condition.
 */

/**
 * "Cryogenic -150 C" — how a storage profile is named wherever a product's
 * storage is chosen: the server's label, which is the storage temperature
 * table's profile name and already carries its temperature. A readable enum
 * while the reference table loads.
 */
export function profileLabel(condition: StorageCondition, conditions: StorageConditionMeta[]): string {
  const meta = conditions.find(c => c.name === condition)
  if (meta) return meta.label
  // Reference loading or failed: "Vapor phase LN2" still reads, the enum does not.
  const words = condition.toLowerCase().replace(/_/g, ' ').replace(/ln2/g, 'LN2')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Every condition a product may be kept at. Stands in while the reference is
 * loading or failed, so the selects still offer something. The controlled-rate
 * freezer is left out: product passes through one, it does not rest there.
 */
const PERSISTENT_CONDITIONS: StorageCondition[] = [
  'LN2_IMMERSION',
  'VAPOR_PHASE_LN2',
  'ULTRA_LOW_MECHANICAL',
  'FREEZER_MINUS_40',
  'FREEZER_MINUS_20',
  'REFRIGERATED',
  'CONTROLLED_COOL',
  'ROOM_TEMP',
  'CONTROLLED_ROOM_TEMP',
]

/**
 * The Storage Profile select's options: the persistent conditions from the
 * reference (or the fallback list while it is unavailable), plus `current`
 * when it is not among them — a select with no item for its value renders blank.
 */
export function profileOptions(
  conditions: StorageConditionMeta[],
  current: StorageCondition | null,
): StorageCondition[] {
  // A switched-off condition is not offered for a new choice; a row already on
  // one still shows it, via `current` below.
  const options = conditions.length > 0
    ? conditions.filter(c => c.persistent && c.active).map(c => c.name)
    : [...PERSISTENT_CONDITIONS]
  if (current && !options.includes(current)) options.push(current)
  return options
}

/**
 * The server's suggestion for every row still unmapped — what "Apply all
 * suggestions" fills in. A row that is already mapped keeps its mapping: the
 * suggestion is a reading of the text, not a correction of someone's decision.
 */
export function pendingSuggestions(
  rows: StorageTempMappingRow[],
): { storageTemperature: string; temperatureKey: string; condition: StorageCondition }[] {
  const out: { storageTemperature: string; temperatureKey: string; condition: StorageCondition }[] = []
  for (const row of rows) {
    // The server fills `suggestedCondition` on every row; it only matters unmapped.
    if (row.condition === null && row.suggestedCondition !== null) {
      out.push({
        storageTemperature: row.storageTemperature,
        temperatureKey: row.temperatureKey,
        condition: row.suggestedCondition,
      })
    }
  }
  return out
}

/**
 * The rows whose draft differs from what is saved — the one PUT that Save
 * sends. A draft value equal to the saved one is not a change, so setting a row
 * and setting it back leaves nothing to save.
 */
export function changedMappings(
  rows: StorageTempMappingRow[],
  draft: Record<string, StorageCondition | null>,
): StorageTempMappingChange[] {
  const out: StorageTempMappingChange[] = []
  for (const row of rows) {
    if (!(row.temperatureKey in draft)) continue
    const next = draft[row.temperatureKey]
    if (next !== row.condition) out.push({ storageTemperature: row.storageTemperature, condition: next })
  }
  return out
}

/**
 * The rows grouped by storage profile, so the temperatures that share one sit
 * together. Unmapped rows come first, as in the catalog order; profiles follow
 * in the reference table's order (or the fallback list's while it is
 * unavailable), and a profile missing from both goes last. Rows keep their
 * order within a group.
 */
export function groupByProfile(
  rows: StorageTempMappingRow[],
  conditions: StorageConditionMeta[],
): { condition: StorageCondition | null; rows: StorageTempMappingRow[] }[] {
  const order: StorageCondition[] = conditions.length > 0 ? conditions.map(c => c.name) : [...PERSISTENT_CONDITIONS]
  const rank = (c: StorageCondition | null) => {
    if (c === null) return -1
    const i = order.indexOf(c)
    return i === -1 ? order.length : i
  }
  const groups = new Map<StorageCondition | null, StorageTempMappingRow[]>()
  for (const row of rows) {
    const group = groups.get(row.condition)
    if (group) group.push(row)
    else groups.set(row.condition, [row])
  }
  return [...groups.entries()]
    .map(([condition, grouped]) => ({ condition, rows: grouped }))
    .sort((a, b) => rank(a.condition) - rank(b.condition))
}
